import AirPlayButton from './AirPlayButton';
import PlayPauseButton from './PlayPauseButton';
import SkipBackwardButton from './SkipBackwardButton';
import SkipForwardButton from './SkipForwardButton';
import localizationFactory from '../localization';
import FullscreenButton from './FullscreenButton';
import PlayPauseOverlay from './PlayPauseOverlay';
import HideControlsOverlay from './HideControlsOverlay';
import TextTitles from './TextTitles';
import CloseButton from './CloseButton';
import SeekbarFloatingTime from './SeekbarFloatingTime';
import PlatformHelper from 'helpers/PlatformHelper';
import { Translate } from 'next-translate';
const shaka = require('external/shaka-player.ui.debug');

// eslint-disable-next-line
export default function initControls(
  player: ShakaPlayer,
  video: HTMLVideoElement,
  videoContainer: HTMLDivElement,
  locale: string,
  t: Translate,
): ShakaControl {
  //Setting up shaka player UI
  const ui = new shaka.ui.Overlay(player, videoContainer, video);

  // Register our factory with the controls, so controls can create button instances.
  shaka.ui.Controls.registerElement('airplay', new AirPlayButton.Factory(video));
  shaka.ui.Controls.registerElement('play_pause_custom', new PlayPauseButton.Factory());
  shaka.ui.Controls.registerElement('skip_forward', new SkipForwardButton.Factory());
  shaka.ui.Controls.registerElement('skip_backward', new SkipBackwardButton.Factory());
  shaka.ui.Controls.registerElement('fullscreen_custom', new FullscreenButton.Factory());
  shaka.ui.Controls.registerElement('play_pause_overlay', new PlayPauseOverlay.Factory());
  shaka.ui.Controls.registerElement('hide_controls_overlay', new HideControlsOverlay.Factory());
  shaka.ui.Controls.registerElement('titles_container', new TextTitles.Factory());
  shaka.ui.Controls.registerElement('close_button', new CloseButton.Factory());
  shaka.ui.Controls.registerElement('seekbar_floating_time', new SeekbarFloatingTime.Factory());

  let baseControls = [
    'close_button',
    'titles_container',
    'play_pause_overlay',
    'play_pause',
    'skip_forward',
    'skip_backward',
    'time_and_duration',
    'mute',
    'volume',
    'overflow_menu',
    'fullscreen_custom',
    'seekbar_floating_time',
  ];

  const overflowMenuButtons = (function () {
    const buttons = ['captions'];

    if (PlatformHelper.isChrome) {
      buttons.push('cast');
    }
    if (!PlatformHelper.isSafari) {
      buttons.push('quality');
    }

    buttons.push('picture_in_picture');

    return buttons;
  })();
  const overflowMenuButtonsWhenAirPlay = ['captions'];

  if (ui.isMobile()) {
    baseControls = [
      'close_button',
      'hide_controls_overlay',
      'time_and_duration',
      'overflow_menu',
      'fullscreen_custom',
      'mute',
      'play_pause_custom',
      'seekbar_floating_time',
    ];
  }

  // Setting UI configuration JSON object
  const baseColor = 'rgba(255, 255, 255, 0.32)';
  const uiConfig: { [index: string]: string | boolean | { [index: string]: string } | string[] } = {
    enableKeyboardPlaybackControls: false,
    addBigPlayButton: false,
    seekBarColors: {
      base: baseColor,
      buffered: 'rgba(255, 255, 255, .5)',
      played: '#D73231',
    },
    volumeBarColors: {
      base: baseColor,
    },
    overflowMenuButtons,
  };

  // Configuring elements to be displayed on video player control panel
  uiConfig['controlPanelElements'] = baseControls;

  // configure UI
  ui.configure(uiConfig);
  const controls = ui.getControls();
  controls.getCastProxy().changeReceiverId(process.env.NEXT_PUBLIC_CHROMECAST_RECEIVER_APP_ID);

  // add translations for currently picked locale
  const localization = controls.getLocalization();
  localization.insert(locale, localizationFactory(t));
  localization.changeLocale([locale]);

  // listen for AirPlay availability
  if (
    (window as unknown as Window & { WebKitPlaybackTargetAvailabilityEvent: unknown })
      .WebKitPlaybackTargetAvailabilityEvent
  ) {
    video.addEventListener(
      'webkitplaybacktargetavailabilitychanged',
      function (event: Event & { availability: 'available' | 'not-available' }) {
        switch (event.availability) {
          case 'available':
            console.log('Airplay available.');
            uiConfig['controlPanelElements'] = [...baseControls, 'airplay']; // re-init controls with AirPlay button
            break;
          case 'not-available':
            console.log('Airplay not available.');
            uiConfig['controlPanelElements'] = baseControls; // re-init controls without AirPlay button
        }

        ui.configure(uiConfig); //configure UI
      },
    );
  }

  // remove PiP when in AirPlay mode
  let airplayRunning = false;
  video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', function () {
    airplayRunning = !airplayRunning;

    setTimeout(() => {
      uiConfig['overflowMenuButtons'] = airplayRunning ? overflowMenuButtonsWhenAirPlay : overflowMenuButtons;
      ui.configure(uiConfig); //configure UI
    }, 300);
  });

  return controls;
}
